/**
 * PeriodPicker — период для дашбордов: пресеты (7/14/30/90 дней) ИЛИ
 * произвольный диапазон дат (from–to). Значение — Period.
 */

import { useState } from 'react';

export type Period = { days: number } | { from: string; to: string };

/** Период → query-строка для API (?days=N или ?from=..&to=..). */
export function periodQuery(p: Period): string {
  return 'from' in p ? `from=${p.from}&to=${p.to}` : `days=${p.days}`;
}

/** Стабильный ключ для react-query. */
export function periodKey(p: Period): string {
  return 'from' in p ? `${p.from}_${p.to}` : `d${p.days}`;
}

/** Человекочитаемое описание периода (для промптов/подписей). */
export function periodLabel(p: Period): string {
  return 'from' in p ? `${p.from} — ${p.to}` : `${p.days} дней`;
}

const PRESETS = [7, 14, 30, 90];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function PeriodPicker({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const isCustom = 'from' in value;
  const [open, setOpen] = useState(isCustom);
  const [from, setFrom] = useState(isCustom ? value.from : '');
  const [to, setTo] = useState(isCustom ? value.to : todayIso());

  const apply = (f: string, t: string) => {
    if (f && t && f <= t) onChange({ from: f, to: t });
  };

  return (
    <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
      <div className="segmented" role="tablist" aria-label="Период">
        {PRESETS.map((d) => (
          <button
            key={d}
            type="button"
            role="tab"
            aria-selected={!isCustom && value.days === d}
            className={!isCustom && value.days === d ? 'active' : ''}
            onClick={() => { setOpen(false); onChange({ days: d }); }}
          >
            {d} дн.
          </button>
        ))}
        <button
          type="button"
          aria-selected={isCustom}
          className={isCustom ? 'active' : ''}
          onClick={() => setOpen((v) => !v)}
          title="Произвольный период"
        >
          Период…
        </button>
      </div>

      {open && (
        <div className="row" style={{ gap: 6, alignItems: 'center' }}>
          <input type="date" className="input" value={from} max={to || todayIso()}
            style={{ width: 150, padding: '6px 10px' }}
            onChange={(e) => { setFrom(e.target.value); apply(e.target.value, to); }} />
          <span className="muted">—</span>
          <input type="date" className="input" value={to} min={from} max={todayIso()}
            style={{ width: 150, padding: '6px 10px' }}
            onChange={(e) => { setTo(e.target.value); apply(from, e.target.value); }} />
        </div>
      )}
    </div>
  );
}
